import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
  Typography,
} from '@mui/material'
import { Button } from '../Buttons/Button'
import { PaymentItem } from 'components/Modules/DataDisplay/PhysicianSearchGrid'
import { DataGrid } from '@mui/x-data-grid'
import { usePaymentPageToken } from 'api/usePaymentPageToken'
import { TextBox } from '../Typography/TextBox'

export interface ConfirmPaymentDialogProps {
  open: boolean
  handleClose: () => void
  handleConfirm: (token: string) => void
  items: PaymentItem[]
}

function formatToUSD(amount) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return formatter.format(amount);
}

export const ConfirmPaymentDialog = ({
  open,
  handleClose,
  handleConfirm,
  items,
}: ConfirmPaymentDialogProps) => {
  const {data} = usePaymentPageToken({ items: items.map(x => ({
      name: x.firstName + ' ' + x.lastName,
      amount: (+x.amount) as unknown as number,
      licenseNumber: x.license
    }))
  })
const otherdata = data as unknown as { token: string }
console.log(!otherdata?.token)
    return (
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">
          {'Please confirm your selected provider information'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{mb:2}}>
            Please verify that the following provider information is correct
            before proceeding with your payment. If the information is incorrect,
            please return to the previous screen and select the correct individual
            from the Search Physician or License dropdown.
          </DialogContentText>          
          <TextBox sx={{mb: 2}}>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1} sx={{ textDecoration: 'underline' }}>
              PLEASE READ - IMPORTANT NOTICES:
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - Mobile devices are not supported at this time.
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - Payments made through NICA's Pay Online feature will post as “PURCHASE FLORIDA BIRT TALLAHASSEE FL” on your credit card statement.
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - If you have an exemption, the exemption must be submitted prior to payment to ensure that you pay the proper amount. If you are unsure if you qualify for an exemption, please visit: <Link href="https://nica.com/medical-providers/exemptions/#documents-required" target='_BLANK'>Exemptions</Link>. 
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - If you have been instructed to deduct interest from your payment, the amount can only be edited on this screen. If you fail to deduct the proper interest amount, NICA cannot refund it. Please make sure the amount is correct prior to submitting payment.
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - NICA's Pay Online feature allows users to select multiple providers to pay in one transaction.
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - Only once the full amount due has been paid will providers be considered "Paid in Full".
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - If the provider HAS NOT paid in full, because payments take up to two (2) business days to post, the provider will still remain visible in the search, and duplicate payments CAN be made.
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" mb={1}>
              - Once the full amount due is paid, the provider will be noted as being in "Pending" status in the search, and payments will not beaccepted for that provider.
            </Typography>
            <Typography fontWeight="bold" variant="body1" color="#4f4f4f" >
              - The limit for the number of physicians that can be selected for a single payment is 30. If you need to pay for more, please complete this payment process again for the additional physicians.
            </Typography>
          </TextBox>
          <Box sx={{ height: 300, width: '100%' }}>
            <DataGrid
              columns={[
                { field: 'license', headerName: 'License Number', flex: .5 },
                { field: 'firstName', headerName: 'First Name', flex: 1 },
                { field: 'lastName', headerName: 'Last Name', flex: 1 },
                { field: 'amount', headerName: 'Amount', flex: .5, valueFormatter: (params) => formatToUSD(params.value)}
              ]}
              rows={items}
              isRowSelectable={() => false} />
            <TextBox sx={{mt: 2}}><Typography align='right' variant="h5">Total Amount: {formatToUSD(items.reduce((a, b) => a + Number(b.amount), 0))}</Typography></TextBox>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="medium"
            variant="outlined"
            color="warning"
            onClick={handleClose}
          >
            Go Back
          </Button>
          <Button
            size="medium"
            variant="contained"
            color="primary"
            onClick={() => handleConfirm(otherdata?.token ?? '')}
            disabled={!otherdata?.token}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    )
  }
